import { useMutation, useQuery } from '@tanstack/react-query';
import { Boxes, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { describeError } from '../../lib/api.ts';
import {
  api,
  type BundleView,
  type DealView,
  type JobView,
  type PipelineView,
} from '../../lib/client.ts';
import { cn } from '../../lib/utils.ts';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { AddJobForm, JobsTable } from './jobs-table.tsx';

const label = 'text-muted-foreground text-xs font-bold tracking-[0.08em] uppercase';

/**
 * A deal's expanded row (TS-925 follow-up): its bundles, each with its slots and its jobs, then
 * the deal's standalone jobs, and the forms that add a bundle or a job. A slot is a hire: the
 * API counts `slotsUsed` from the bundle's jobs' filled positions, so this only reads it.
 *
 * A job may use a bundle bought on ANY deal of its client (2026-11-03): the client's bundles are
 * read once (`GET /projects/{id}/bundles`), a deal draws the ones it bought, each listing all its
 * jobs whichever deal they sit under, and a job's picker offers every bundle of the client,
 * naming the deal of one bought elsewhere.
 *
 * Until the bundles have loaded nothing is grouped and nothing about bundles is offered: while
 * loading, a status line; on a failure, the error with Retry and the deal's jobs ungrouped, with
 * no Add bundle (which could duplicate one that exists). Bundles are the project's (`canWrite`,
 * `customer.update`); the jobs and their pickers are the job permissions' (`canWriteJobs`,
 * `canCreateJobs`), as on the Jobs tab. The project page's refresh re-reads every `['bundles']`,
 * so a `filled` change anywhere on it moves the slot meters.
 */
export function DealJobs({
  deal,
  deals,
  jobs,
  projectJobs,
  jobPipeline,
  shortlistPipeline,
  canWrite,
  canWriteJobs,
  canCreateJobs,
  onChanged,
}: {
  deal: DealView;
  deals: DealView[];
  /** The deal's own jobs. */
  jobs: JobView[];
  /** Every job of the project: a bundle's box lists its jobs whichever deal they sit under. */
  projectJobs: JobView[];
  jobPipeline: PipelineView | undefined;
  shortlistPipeline: PipelineView | undefined;
  /** `customer.update` on the project: bundles are added, edited and deleted with it. */
  canWrite: boolean;
  /** `job.update`: the jobs' status and bundle pickers. */
  canWriteJobs: boolean;
  /** `job.create`: Add job. */
  canCreateJobs: boolean;
  onChanged: () => Promise<void>;
}) {
  // The client's bundles: a job may use one bought on any deal of its project (2026-11-03).
  const bundlesQuery = useQuery({
    queryKey: ['bundles', 'project', deal.projectId],
    queryFn: () => api.bundles.ofProject(deal.projectId),
  });
  const tableProps = {
    pipeline: jobPipeline,
    shortlistPipeline,
    canWrite: canWriteJobs,
    onChanged,
  };
  if (!bundlesQuery.isSuccess)
    return (
      <div className="flex flex-col gap-5">
        {bundlesQuery.isError ? (
          <div className="flex flex-wrap items-center gap-3" role="alert">
            <p className="text-danger text-sm">
              Couldn’t load this deal’s bundles: {describeError(bundlesQuery.error)}
            </p>
            <Button size="sm" variant="outline" onClick={() => void bundlesQuery.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm" role="status">
            Loading bundles…
          </p>
        )}
        <section className="flex flex-col gap-3" aria-label="Jobs under this deal">
          <p className={label}>Jobs under this deal</p>
          <JobsTable jobs={jobs} {...tableProps} />
        </section>
        {canCreateJobs && <AddJobForm deals={deals} dealId={deal.id} onCreated={onChanged} />}
      </div>
    );
  // This deal's own bundles are drawn here; the picker offers all of the client's, naming the
  // deal of one bought elsewhere.
  const bundles = bundlesQuery.data.filter((b) => b.dealId === deal.id);
  const titleOf = (dealId: string) => deals.find((x) => x.id === dealId)?.title ?? 'another deal';
  const choices = bundlesQuery.data.map((b) =>
    b.dealId === deal.id ? b : { ...b, name: `${b.name} · ${titleOf(b.dealId)}` },
  );
  const standalone = jobs.filter((j) => !j.bundleId || !bundles.some((b) => b.id === j.bundleId));
  const rows = { ...tableProps, bundles: choices };
  return (
    <div className="flex flex-col gap-5">
      {bundles.map((b) => (
        <BundleSection
          key={b.id}
          bundle={b}
          canWrite={canWrite}
          onChanged={onChanged}
          table={<JobsTable jobs={projectJobs.filter((j) => j.bundleId === b.id)} {...rows} />}
        />
      ))}
      <section className="flex flex-col gap-3" aria-label="Standalone jobs">
        <p className={label}>{bundles.length ? 'Standalone jobs' : 'Jobs under this deal'}</p>
        <JobsTable jobs={standalone} {...rows} />
      </section>
      {(canCreateJobs || canWrite) && (
        <div className="flex flex-col gap-3">
          {canCreateJobs && (
            <AddJobForm deals={deals} dealId={deal.id} bundles={choices} onCreated={onChanged} />
          )}
          {canWrite && <AddBundleForm dealId={deal.id} onCreated={onChanged} />}
        </div>
      )}
    </div>
  );
}

/** "2 of 3 slots used" and a bar; an overfilled bundle (more hires than bought) says so. */
export function SlotMeter({ bundle }: { bundle: BundleView }) {
  const { slotsUsed: used, slotsPurchased: bought } = bundle;
  const over = used > bought;
  return (
    <span className="flex min-w-40 flex-col gap-1" data-testid="slot-meter">
      <span className="text-xs tabular-nums">
        <span className="font-semibold">{used}</span> of {bought} slot{bought === 1 ? '' : 's'} used
        {over ? (
          <span className="text-danger"> · {used - bought} over</span>
        ) : (
          <span className="text-muted-foreground"> · {bundle.slotsUnused} open</span>
        )}
      </span>
      <span
        className="bg-muted h-1.5 w-full overflow-hidden rounded-full"
        role="meter"
        aria-label={`${bundle.name} slots used`}
        aria-valuemin={0}
        aria-valuemax={bought}
        aria-valuenow={Math.min(used, bought)}
      >
        <span
          className={cn('block h-full rounded-full', over ? 'bg-danger' : 'bg-brand')}
          style={{ width: `${Math.min(100, (used / bought) * 100)}%` }}
        />
      </span>
    </span>
  );
}

function BundleSection({
  bundle,
  canWrite,
  onChanged,
  table,
}: {
  bundle: BundleView;
  canWrite: boolean;
  onChanged: () => Promise<void>;
  table: ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: () => api.bundles.remove(bundle.id),
    onSuccess: async () => {
      setError(null);
      await onChanged();
    },
    onError: (err) => setError(describeError(err)),
  });
  return (
    <section
      className="border-border bg-card/60 flex flex-col gap-3 rounded-2xl border p-4"
      aria-label={`Bundle ${bundle.name}`}
      data-testid="bundle"
      data-bundle-id={bundle.id}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="flex min-w-40 flex-1 items-center gap-3">
          <span className="bg-brand-soft text-brand-soft-foreground grid size-8 shrink-0 place-items-center rounded-lg">
            <Boxes className="size-4" aria-hidden />
          </span>
          <span className="min-w-0 text-sm font-semibold break-words" data-testid="bundle-name">
            {bundle.name}
          </span>
          {bundle.package && <Badge variant="brand">{bundle.package}</Badge>}
        </span>
        <SlotMeter bundle={bundle} />
        <Badge variant="outline">
          {bundle.jobCount} job{bundle.jobCount === 1 ? '' : 's'}
        </Badge>
        {canWrite && (
          <span className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Edit ${bundle.name}`}
              aria-expanded={editing}
              onClick={() => setEditing((v) => !v)}
            >
              <Pencil />
            </Button>
            {bundle.jobCount === 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="text-danger hover:text-danger"
                aria-label={`Delete ${bundle.name}`}
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                <Trash2 />
              </Button>
            )}
          </span>
        )}
      </div>
      {editing && (
        <BundleForm
          initial={bundle}
          submitLabel="Save bundle"
          onSubmit={(_all, changed) => api.bundles.patch(bundle.id, changed)}
          onUnchanged={() => setEditing(false)}
          onDone={async () => {
            setEditing(false);
            await onChanged();
          }}
        />
      )}
      {table}
      {error && (
        <p className="text-danger text-sm" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

function AddBundleForm({ dealId, onCreated }: { dealId: string; onCreated: () => Promise<void> }) {
  return (
    <BundleForm
      submitLabel="Add bundle"
      onSubmit={(all) => api.bundles.create(dealId, all)}
      onDone={onCreated}
      dashed
    />
  );
}

type BundleFields = { name: string; package: string | null; slotsPurchased: number };

/**
 * Name, package and slots: the add form (empty) and a bundle's edit form (filled in). An edit
 * sends only the fields changed in the form, so it never puts back what someone else changed
 * meanwhile (a form left open would otherwise revert their slots); nothing changed, no request.
 */
function BundleForm({
  initial,
  submitLabel,
  onSubmit,
  onDone,
  onUnchanged,
  dashed,
}: {
  initial?: BundleView;
  submitLabel: string;
  /** Every field, and those changed from `initial` (all of them on the add form). */
  onSubmit: (all: BundleFields, changed: Partial<BundleFields>) => Promise<unknown>;
  onDone: () => Promise<void>;
  onUnchanged?: () => void;
  dashed?: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [pkg, setPkg] = useState(initial?.package ?? '');
  const [slots, setSlots] = useState(String(initial?.slotsPurchased ?? 3));
  const [error, setError] = useState<string | null>(null);
  const count = Number(slots);
  const valid = name.trim() !== '' && Number.isInteger(count) && count >= 1;
  const fields: BundleFields = {
    name: name.trim(),
    package: pkg.trim() || null,
    slotsPurchased: count,
  };
  const changed = initial
    ? (Object.fromEntries(
        (Object.keys(fields) as Array<keyof BundleFields>)
          .filter((k) => fields[k] !== initial[k])
          .map((k) => [k, fields[k]]),
      ) as Partial<BundleFields>)
    : fields;
  const save = useMutation({
    mutationFn: () => onSubmit(fields, changed),
    onSuccess: async () => {
      if (!initial) {
        setName('');
        setPkg('');
        setSlots('3');
      }
      setError(null);
      await onDone();
    },
    onError: (err) => setError(describeError(err)),
  });
  return (
    <form
      className={cn(
        'flex flex-wrap items-center gap-3',
        dashed && 'border-border bg-card/50 rounded-2xl border border-dashed p-3',
      )}
      data-testid={initial ? 'edit-bundle' : 'add-bundle'}
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        if (Object.keys(changed).length === 0) onUnchanged?.();
        else save.mutate();
      }}
    >
      <Input
        aria-label="Bundle name"
        placeholder="New bundle name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="bg-card w-full sm:w-56"
      />
      <Input
        aria-label="Package"
        placeholder="Package (e.g. 3VA)"
        value={pkg}
        maxLength={60}
        onChange={(e) => setPkg(e.target.value)}
        className="bg-card w-40"
      />
      <Input
        aria-label="Slots purchased"
        type="number"
        min={1}
        step={1}
        value={slots}
        onChange={(e) => setSlots(e.target.value)}
        className="bg-card w-24"
      />
      <Button
        type="submit"
        variant={initial ? 'default' : 'outline'}
        disabled={!valid || save.isPending}
      >
        {initial ? null : <Plus />} {submitLabel}
      </Button>
      {error && (
        <p className="text-danger w-full text-sm" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
